import { useCallback, useEffect, useState } from "react";

// Media Queries
const xSmallVerticalMedia = window.matchMedia(
	"(max-width: 599.99px) and (orientation: portrait)",
);
const xSmallHorizontalMedia = window.matchMedia(
	"(max-width: 599.99px) and (orientation: landscape)",
);
const smallVerticalMedia = window.matchMedia(
	"(min-width: 600px) and (max-height: 959.99px) and (orientation: portrait)",
);
const smallHorizontalMedia = window.matchMedia(
	"(min-width: 600px) and (max-width: 959.99px) and (orientation: landscape)",
);
const mediumVerticalMedia = window.matchMedia(
	"(min-width: 600px) and (min-height: 960px) and (orientation: portrait)",
);
const mediumHorizontalMedia = window.matchMedia(
	"(min-width: 960px) and (max-width: 1380.99px) and (orientation: landscape)",
);
const largeMedia = window.matchMedia("(min-width: 1381px)");

const allMediaQueries = [
	xSmallVerticalMedia,
	xSmallHorizontalMedia,
	smallVerticalMedia,
	smallHorizontalMedia,
	mediumVerticalMedia,
	mediumHorizontalMedia,
	largeMedia,
];

// Types
type ScreenSize = {
	isXSmallVertical: boolean;
	isXSmallHorizontal: boolean;
	isSmallVertical: boolean;
	isSmallHorizontal: boolean;
	isMediumVertical: boolean;
	isMediumHorizontal: boolean;
	isLarge: boolean;
};

type DeviceInfo = {
	isMobile: boolean;
	isTablet: boolean;
	isDesktop: boolean;
	width: number;
	height: number;
	orientation: "portrait" | "landscape";
	screenSizeClass: string;
	screenSize: ScreenSize;
};

let handlers: (() => void)[] = [];

const subscribe = (handler: () => void) => handlers.push(handler);
const unsubscribe = (handler: () => void) => {
	handlers = handlers.filter((item) => item !== handler);
};

for (const media of allMediaQueries) {
	media.addEventListener("change", () => {
		for (const handler of handlers) {
			handler();
		}
	});
}

function getScreenSize(): ScreenSize {
	// console.log({
	// 	isXSmallVertical: xSmallVerticalMedia.matches,
	// 	isXSmallHorizontal: xSmallHorizontalMedia.matches,
	// 	isSmallVertical: smallVerticalMedia.matches,
	// 	isSmallHorizontal: smallHorizontalMedia.matches,
	// 	isMediumVertical: mediumVerticalMedia.matches,
	// 	isMediumHorizontal: mediumHorizontalMedia.matches,
	// 	isLarge: largeMedia.matches,
	// });
	return {
		isXSmallVertical: xSmallVerticalMedia.matches,
		isXSmallHorizontal: xSmallHorizontalMedia.matches,
		isSmallVertical: smallVerticalMedia.matches,
		isSmallHorizontal: smallHorizontalMedia.matches,
		isMediumVertical: mediumVerticalMedia.matches,
		isMediumHorizontal: mediumHorizontalMedia.matches,
		isLarge: largeMedia.matches,
	};
}

function getScreenSizeClass(screenSize: ScreenSize): string {
	if (screenSize.isLarge) {
		return "screen-large";
	}
	if (screenSize.isMediumHorizontal) {
		return "screen-medium-horizontal";
	}
	if (screenSize.isMediumVertical) {
		return "screen-medium-vertical";
	}
	if (screenSize.isSmallHorizontal) {
		return "screen-small-horizontal";
	}
	if (screenSize.isSmallVertical) {
		return "screen-small-vertical";
	}
	if (screenSize.isXSmallHorizontal) {
		return "screen-x-small-horizontal";
	}
	return "screen-x-small-vertical";
}

export const useDevice = () => {
	const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
		const screenSize = getScreenSize();
		return {
			isMobile: window.innerWidth < 768,
			isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
			isDesktop: window.innerWidth >= 1024,
			width: window.innerWidth,
			height: window.innerHeight,
			orientation:
				window.innerHeight > window.innerWidth ? "portrait" : "landscape",
			screenSizeClass: getScreenSizeClass(screenSize),
			screenSize,
		};
	});

	const handleChange = useCallback(() => {
		const screenSize = getScreenSize();
		setDeviceInfo({
			isMobile: window.innerWidth < 768,
			isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
			isDesktop: window.innerWidth >= 1024,
			width: window.innerWidth,
			height: window.innerHeight,
			orientation:
				window.innerHeight > window.innerWidth ? "portrait" : "landscape",
			screenSizeClass: getScreenSizeClass(screenSize),
			screenSize,
		});
	}, []);

	useEffect(() => {
		subscribe(handleChange);

		window.addEventListener("resize", handleChange);

		return () => {
			unsubscribe(handleChange);
			window.removeEventListener("resize", handleChange);
		};
	}, [handleChange]);

	const getDeviceType = () => {
		if (deviceInfo.isMobile) {
			return "mobile";
		}
		if (deviceInfo.isTablet) {
			return "tablet";
		}
		return "desktop";
	};

	return {
		...deviceInfo,
		getDeviceType,
	};
};

export const useScreenSize = () => {
	const { screenSize } = useDevice();
	return screenSize;
};

export const useScreenSizeClass = () => {
	const { screenSizeClass } = useDevice();
	return screenSizeClass;
};

// Ejemplo de uso:
/*
import { useDevice } from './useDevice';

const MyComponent = () => {
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    width,
    height,
    orientation,
    screenSizeClass,
    screenSize,
    getDeviceType 
  } = useDevice();

  return (
    <div>
      <p>Tipo de dispositivo: {getDeviceType()}</p>
      <p>Orientación: {orientation}</p>
      <p>Clase de tamaño: {screenSizeClass}</p>
      <p>Dimensiones: {width}x{height}</p>
      <pre>{JSON.stringify(screenSize, null, 2)}</pre>
    </div>
  );
};
*/
